import React, { useEffect, useState } from 'react';
import { Download, Mic, Share, X } from 'lucide-react';
import { useRecordingSessionStore } from '@/stores/useRecordingSessionStore';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import {
  hasDeferredInstallPrompt,
  isIOSSafari,
  subscribeInstallPrompt,
  triggerInstallPrompt,
} from '@/lib/installPrompt';

/**
 * Shown the first time a doctor on a non-installed mobile/iPad web
 * context taps Start. Mirrors the attestation-modal pattern: the store
 * defers the start and parks the init in `awaitingInstallNudge`; this
 * modal resolves the deferral via `confirmInstallNudge` (proceed) or
 * `cancelInstallNudge` (back out). Either path marks the nudge as shown
 * so the doctor isn't pestered every click.
 *
 * Three states by platform:
 *   - Chrome/Android (or any browser that fired beforeinstallprompt):
 *     "Install MAIE" CTA triggers the cached prompt.
 *   - iOS Safari (no beforeinstallprompt event ever):
 *     "Add to Home Screen" instructions; primary CTA is "Continue in
 *     browser" since we can't trigger it programmatically.
 *   - Other (in-app browsers, edge mobile UAs without a captured
 *     prompt yet): just the "Continue in browser" path.
 *
 * Either path leads to recording proceeding. The modal is a one-time
 * nudge, not a blocker.
 */
export const InstallNudgeModal: React.FC = () => {
  const { isRTL } = useTranslation();
  const awaiting = useRecordingSessionStore((s) => s.awaitingInstallNudge);
  const confirm = useRecordingSessionStore((s) => s.confirmInstallNudge);
  const cancel = useRecordingSessionStore((s) => s.cancelInstallNudge);

  const [hasPrompt, setHasPrompt] = useState(hasDeferredInstallPrompt);
  const [installing, setInstalling] = useState(false);

  // The browser may fire beforeinstallprompt slightly after the modal
  // opens (engagement heuristics race). Subscribe so the Install CTA
  // appears the moment a prompt becomes available.
  useEffect(() => {
    return subscribeInstallPrompt(() => setHasPrompt(hasDeferredInstallPrompt()));
  }, []);

  if (!awaiting) return null;

  const ios = isIOSSafari();

  const handleInstall = async () => {
    setInstalling(true);
    try {
      const outcome = await triggerInstallPrompt();
      // Whether accepted or dismissed, proceed with the recording — the
      // session shouldn't be held hostage to the OS install dialog.
      // `appinstalled` will fire async if accepted; we don't wait.
      void outcome;
      await confirm();
    } finally {
      setInstalling(false);
    }
  };

  const handleContinue = async () => {
    await confirm();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) cancel();
      }}
    >
      <div
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border shadow-xl"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border-primary)',
        }}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <header
          className="flex items-start justify-between gap-3 px-5 sm:px-6 pt-5 pb-4 border-b"
          style={{ borderColor: 'var(--color-border-primary)' }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Mic
              className="w-4 h-4 flex-shrink-0"
              style={{ color: 'var(--color-primary-700)' }}
            />
            <h3
              className="text-base font-semibold tracking-tight"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {isRTL ? 'قبل أول تسجيل' : 'Before your first recording'}
            </h3>
          </div>
          <button
            type="button"
            onClick={cancel}
            className="inline-flex items-center justify-center min-h-11 min-w-11 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:ring-offset-2"
            aria-label={isRTL ? 'إغلاق' : 'Close'}
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="px-5 sm:px-6 py-5 space-y-4">
          <p
            className="text-sm leading-relaxed"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {isRTL
              ? 'تثبيت MAIE على جهازك يمنحك تسجيلًا أسرع، وعملًا في الخلفية، وتفعيل المايكروفون بدون فتح المتصفح. يمكنك المتابعة في المتصفح الآن إن أردت.'
              : 'Installing MAIE on this device gives you faster startup, background recording, and lets the mic stay active when the screen locks. You can keep going in the browser if you’d rather.'}
          </p>

          {ios && (
            <div
              className="flex items-start gap-3 p-3 rounded-lg"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border-primary)',
              }}
            >
              <Share
                className="w-4 h-4 flex-shrink-0 mt-0.5"
                style={{ color: 'var(--color-primary-700)' }}
              />
              <div className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                {isRTL ? (
                  <>
                    لتثبيت على iOS: اضغط زر <strong>المشاركة</strong> ثم
                    اختر <strong>إضافة إلى الشاشة الرئيسية</strong>.
                  </>
                ) : (
                  <>
                    To install on iOS: tap the <strong>Share</strong> button,
                    then choose <strong>Add to Home Screen</strong>.
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <footer
          className="flex flex-col-reverse sm:flex-row gap-2 px-5 sm:px-6 pb-5 pt-2"
        >
          <Button
            variant="outline"
            onClick={handleContinue}
            className="flex-1"
          >
            {isRTL ? 'المتابعة في المتصفح' : 'Continue in browser'}
          </Button>
          {!ios && (
            <Button
              onClick={handleInstall}
              disabled={!hasPrompt || installing}
              loading={installing}
              className="flex-1"
            >
              {!installing && <Download />}
              {installing
                ? isRTL ? 'جارٍ التثبيت…' : 'Installing…'
                : isRTL ? 'تثبيت MAIE' : 'Install MAIE'}
            </Button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default InstallNudgeModal;
